/**
 * Variant D — full-screen overlay (founder's idea, 2026-05-24).
 *
 * Fullscreen takeover for a single-tap deep peek: identity + balance
 * + 4 quick actions + the 5 latest activities (recent trades, social
 * follows, payouts). Pattern matches Cash App's "Activity" tab and
 * Robinhood's "Account" screen — the user navigates INTO the
 * dashboard rather than peeking around a modal. Trade-off vs the
 * smaller sheet/popover: more content, less context, no swipe-down
 * mental model for dismissal.
 *
 * For users who want a Robinhood-style account screen, this is the
 * right answer; for users who just want a balance check, the sheet
 * (variant A) is faster.
 */
"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowLeft,
  ChevronRight,
  Briefcase,
  ClipboardList,
  Users,
  Star,
  Gift,
  Receipt,
  ShieldCheck,
  Settings as SettingsIcon,
  LifeBuoy,
  type LucideIcon,
} from "lucide-react";
import { QuickDashboardLean } from "./QuickDashboardLean";

interface NavItem {
  label: string;
  hint?: string;
  icon: LucideIcon;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "Portfolio",
    hint: "Positions, orders, P/L",
    icon: Briefcase,
    href: "/portfolio",
  },
  {
    label: "Transaction history",
    hint: "Trades, deposits, bonuses",
    icon: Receipt,
    href: "/dashboard/transactions",
  },
  {
    label: "Manage subscriptions",
    hint: "Creators you follow",
    icon: Star,
    href: "/dashboard/creators",
  },
  {
    label: "Copytraders",
    hint: "PI traders you mirror",
    icon: Users,
    href: "/dashboard/copytraders",
  },
  {
    label: "Open orders",
    hint: "Active limit orders",
    icon: ClipboardList,
    href: "/home?tab=orders",
  },
  {
    label: "Rewards",
    hint: "Tier + monthly bonus",
    icon: Gift,
    href: "/rewards",
  },
  {
    label: "Security",
    hint: "Password, 2FA, sessions",
    icon: ShieldCheck,
    href: "/settings/security",
  },
  {
    label: "Settings",
    hint: "Profile, region, display",
    icon: SettingsIcon,
    href: "/settings",
  },
  {
    label: "Help & support",
    icon: LifeBuoy,
    href: "/help",
  },
];

interface ActivityRow {
  id: string;
  title: string;
  detail: string;
  amount: string;
  amountPositive: boolean;
  minutesAgo: number;
}

const RECENT_5: ActivityRow[] = [
  {
    id: "a1",
    title: "Brazil wins 2026 FIFA World Cup",
    detail: "Bought 30 YES @ 30¢",
    amount: "−$9.00",
    amountPositive: false,
    minutesAgo: 4,
  },
  {
    id: "a2",
    title: "Will Bitcoin close 2026 above $200K?",
    detail: "Position closed · resolved YES",
    amount: "+$24.50",
    amountPositive: true,
    minutesAgo: 56,
  },
  {
    id: "a3",
    title: "Daily login reward",
    detail: "Diamond tier · day 12",
    amount: "+$2.00",
    amountPositive: true,
    minutesAgo: 312,
  },
  {
    id: "a4",
    title: "@whale_alpha copy-trade",
    detail: "Mirrored YES on Lula 2026",
    amount: "−$15.00",
    amountPositive: false,
    minutesAgo: 480,
  },
  {
    id: "a5",
    title: "Referral bonus",
    detail: "@friend123 placed first trade",
    amount: "+$10.00",
    amountPositive: true,
    minutesAgo: 1440,
  },
];

export function QuickDashboardFullScreen({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Label what we're going back to — feels like iOS nav.
  const backLabel = labelForPath(pathname);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Account"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        background: "var(--color-bg)",
        overflowY: "auto",
        // Reserve room at the bottom so content doesn't slip under
        // the fixed back-button bar. 76px = button height (~56px) +
        // padding ring (~20px), plus iPhone home-indicator safe area.
        paddingBottom: "calc(76px + env(safe-area-inset-bottom))",
        animation: "v6-fullscreen-in 0.22s ease",
      }}
    >
      <style>{`
        @keyframes v6-fullscreen-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* Top bar — iOS-style back affordance on the left.
       * 2026-05-24 PM founder direction: easier to close + go back to
       * navigation. Bigger 44×44 hit target (Apple HIG minimum), label
       * shows the SOURCE route ("Back to Markets" / "Back to Home")
       * so the user knows where they came from. Tap anywhere on the
       * back chip dismisses. */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between border-b px-3 py-2"
        style={{
          background: "var(--color-bg)",
          borderColor: "var(--color-border)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={`Back to ${backLabel}`}
          className="-ml-1 flex h-11 items-center gap-1.5 rounded-lg px-3 text-[14px] font-semibold transition-colors"
          style={{ color: "var(--color-text)" }}
        >
          <ArrowLeft size={18} strokeWidth={2} aria-hidden="true" />
          <span>{backLabel}</span>
        </button>
        <span
          className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: "var(--color-text3)" }}
        >
          Account
        </span>
        <span className="w-11" aria-hidden="true" />
      </div>

      {/* Lean content (identity + balance + 4 quick actions + View full
          dashboard CTA) */}
      <QuickDashboardLean onClose={onClose} />

      {/* Navigation menu — Coinbase-style account list. Second
       * section of the short menu (founder direction 2026-05-24:
       * "add the navigation menu as the second part menu as like
       * coinbase, with similar clone"). Vertical rows with leading
       * icon + label + trailing chevron, all rounded into one card
       * with hairline row dividers. */}
      <section className="px-5 pb-2">
        <h2
          className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: "var(--color-text3)" }}
        >
          Navigation
        </h2>
        <nav
          className="overflow-hidden rounded-xl border"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
          }}
        >
          {NAV_ITEMS.map((item, idx) => (
            <NavRow
              key={item.href}
              item={item}
              first={idx === 0}
              onSelect={onClose}
            />
          ))}
        </nav>
      </section>

      {/* Region picker lives in the footer now (Polymarket-style
       * discrete entry, 2026-05-24). RegionDetector still
       * auto-detects on first visit. */}

      {/* Latest 5 activity */}
      <section className="px-5 pb-8">
        <div className="mb-3 flex items-center justify-between">
          <h2
            className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: "var(--color-text3)" }}
          >
            Latest activity
          </h2>
          <Link
            href="/feed/activity"
            onClick={onClose}
            className="text-[12px] font-medium"
            style={{ color: "var(--color-accent)" }}
          >
            See all →
          </Link>
        </div>
        <ul
          className="overflow-hidden rounded-xl border"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface)",
          }}
        >
          {RECENT_5.map((row, idx) => (
            <li
              key={row.id}
              className="flex items-center gap-3 px-3 py-3"
              style={{
                borderTop:
                  idx === 0 ? "none" : "1px solid var(--color-border)",
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">
                  {row.title}
                </div>
                <div
                  className="mt-0.5 truncate text-[11px]"
                  style={{ color: "var(--color-text3)" }}
                >
                  {row.detail} · {formatMinutesAgo(row.minutesAgo)}
                </div>
              </div>
              <span
                className="flex-shrink-0 font-mono text-[13px] font-bold tabular-nums"
                style={{
                  color: row.amountPositive
                    ? "var(--color-yes)"
                    : "var(--color-no)",
                }}
              >
                {row.amount}
              </span>
              <ChevronRight
                size={14}
                strokeWidth={1.75}
                aria-hidden="true"
                style={{ color: "var(--color-text3)" }}
              />
            </li>
          ))}
        </ul>
      </section>

      {/* Sticky back-bar — pinned to the viewport bottom so it's
       * always thumb-reachable regardless of scroll position.
       * Founder direction 2026-05-24: "the back button should be
       * fixed on the bottom". The parent panel reserves bottom
       * padding (76px + safe-area) so the activity list isn't hidden
       * underneath. */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 71,
          padding: "12px 16px",
          paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
          background: "var(--color-bg)",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[14px] font-bold"
          style={{
            background: "var(--color-surface2)",
            color: "var(--color-text)",
            border: "1px solid var(--color-border)",
          }}
        >
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          Back to {backLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Human label for the route the user came from. Used in the top
 * back-button and bottom dismiss CTA so the user always knows where
 * the panel will return them.
 */
function labelForPath(pathname: string | null): string {
  if (!pathname || pathname === "/" || pathname === "/v6") return "Home";
  if (pathname.startsWith("/markets")) return "Markets";
  if (pathname.startsWith("/feed")) return "Feed";
  if (pathname.startsWith("/home")) return "Home";
  if (pathname.startsWith("/dashboard")) return "Dashboard";
  if (pathname.startsWith("/settings")) return "Settings";
  if (pathname.startsWith("/admin")) return "Admin";
  if (pathname.startsWith("/promos")) return "Home";
  return "Back";
}

function formatMinutesAgo(mins: number): string {
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Single navigation row — leading icon tile + label/hint stack +
 * trailing chevron. Coinbase / Apple-Settings pattern; the icon
 * tile uses an ochre-tinted background so the eye lands on it
 * before scanning the label.
 */
function NavRow({
  item,
  first,
  onSelect,
}: {
  item: NavItem;
  first: boolean;
  onSelect: () => void;
}) {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      onClick={onSelect}
      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[var(--color-surface2)]"
      style={{
        borderTop: first ? "none" : "1px solid var(--color-border)",
        color: "var(--color-text)",
        textDecoration: "none",
      }}
    >
      <span
        aria-hidden="true"
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px]"
        style={{
          background:
            "color-mix(in srgb, var(--color-accent) 14%, transparent)",
          color: "var(--color-accent)",
        }}
      >
        <Icon size={16} strokeWidth={2} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold">{item.label}</div>
        {item.hint && (
          <div
            className="mt-0.5 truncate text-[11px]"
            style={{ color: "var(--color-text3)" }}
          >
            {item.hint}
          </div>
        )}
      </div>
      <ChevronRight
        size={14}
        strokeWidth={1.75}
        aria-hidden="true"
        style={{ color: "var(--color-text3)" }}
      />
    </Link>
  );
}
